// Thumbnails need the Authorization header, which <img> cannot send, so each is fetched as a blob and
// shown through an object URL. This cache keeps the most recent ones, revokes the URLs it evicts,
// shares one request between callers, and limits how many fetches run at once.
export type Fetcher = (id: number, signal?: AbortSignal) => Promise<Blob>;

export class ThumbCache {
  private urls = new Map<number, string>();          // insertion order = recency
  private inflight = new Map<number, Promise<string>>();
  private queue: (() => void)[] = [];
  private running = 0;

  constructor(
    private fetcher: Fetcher,
    private max = 400,
    private concurrency = 6,
    private makeUrl: (b: Blob) => string = (b) => URL.createObjectURL(b),
    private revoke: (u: string) => void = (u) => URL.revokeObjectURL(u),
  ) {}

  get size() { return this.urls.size; }

  get(id: number, signal?: AbortSignal): Promise<string> {
    const hit = this.urls.get(id);
    if (hit) { this.urls.delete(id); this.urls.set(id, hit); return Promise.resolve(hit); }
    const pending = this.inflight.get(id);
    if (pending) return pending;
    const p = this.schedule(() => this.fetcher(id, signal)).then((blob) => {
      const url = this.makeUrl(blob);
      this.urls.set(id, url);
      this.trim();
      return url;
    }).finally(() => this.inflight.delete(id));
    this.inflight.set(id, p);
    return p;
  }

  /** Drop one entry (an image was deleted) or everything (the project changed). */
  clear(id?: number) {
    if (id !== undefined) { const u = this.urls.get(id); if (u) this.revoke(u); this.urls.delete(id); return; }
    for (const u of this.urls.values()) this.revoke(u);
    this.urls.clear();
  }

  private trim() {
    while (this.urls.size > this.max) {
      const [oldest, url] = this.urls.entries().next().value as [number, string];
      this.urls.delete(oldest);
      this.revoke(url);
    }
  }

  private schedule<T>(job: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const run = () => {
        this.running++;
        job().then(resolve, reject).finally(() => { this.running--; this.queue.shift()?.(); });
      };
      this.running < this.concurrency ? run() : this.queue.push(run);
    });
  }
}
